import React, { useEffect, useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, money } from '@/lib/lenderDemo';

const STATUS_CHIP = {
  on_track: { label: 'On track', bg: '#ECFDF3', fg: '#067647' },
  closed: { label: 'Closed', bg: '#F1F5F9', fg: '#64748B' },
};

const TITLES = {
  loan_officer: { h: 'My loans', sub: 'Every loan you originated, where each one stands, and what needs you today.' },
  construction_dept: { h: 'Construction loans', sub: "Every lender's loans. Loan officers approve first; final approval happens here." },
  bank_admin: { h: 'All construction loans', sub: 'Manage bank users and assign loans to loan officers.' },
};

function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color: LENDER_NAVY }}>{value}</div>
    </div>
  );
}

export default function LenderLoans() {
  const actor = getLenderActor();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    if (!actor?.id) return;
    apiService.lenderLoans(actor.id).then(({ data }) => setData(data)).catch(() => setData({ loans: [], kpis: {} }));
  }, [actor?.id]);
  useEffect(() => { load(); }, [load]);

  const assign = async (loanId, officerId) => {
    await apiService.lenderAssignLoan(actor.id, loanId, officerId);
    load();
  };

  const role = actor?.role;
  const showOfficer = role === 'construction_dept' || role === 'bank_admin';
  const t = TITLES[role] || TITLES.loan_officer;
  const loans = (data?.loans || []).filter((l) => {
    if (!q) return true;
    const hay = `${l.borrower_name} ${l.project_name} ${l.city} ${l.state} ${l.loan_officer_name}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const k = data?.kpis || {};

  return (
    <LenderShell active="loans">
      <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{t.h}</h1>
      <p className="text-slate-500 text-sm mb-5">{t.sub}</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi label="Total loans" value={k.total_loans ?? '—'} />
        <Kpi label="Active loans" value={k.active_loans ?? '—'} />
        <Kpi label="Funded to date" value={money(k.funded_to_date)} />
        <Kpi label="Remaining funds" value={money(k.remaining_funds)} />
        <Kpi label="Closed loans" value={k.closed_loans ?? '—'} />
      </div>

      <div className="relative mb-3 max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="lender-loans-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Borrower, project, city, state or officer"
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3">Borrower / Project</th>
              <th className="px-4 py-3">City, State</th>
              {showOfficer && <th className="px-4 py-3">Loan officer</th>}
              <th className="px-4 py-3 text-right">Budget</th>
              <th className="px-4 py-3 text-right">Funded to date</th>
              <th className="px-4 py-3 text-right">Remaining</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loans.map((l) => {
              const chip = STATUS_CHIP[l.status] || STATUS_CHIP.on_track;
              return (
                <tr key={l.id} data-testid={`lender-loan-row-${l.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="font-semibold" style={{ color: LENDER_NAVY }}>{l.borrower_name}</div>
                    <div className="text-xs text-slate-400">{l.project_name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                  {showOfficer && (
                    <td className="px-4 py-3">
                      {role === 'bank_admin' ? (
                        <select
                          data-testid={`lender-assign-${l.id}`}
                          value={l.assigned_loan_officer_id || ''}
                          onChange={(e) => assign(l.id, e.target.value)}
                          className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
                        >
                          <option value="" disabled>Unassigned</option>
                          {(data?.loan_officers || []).map((o) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-slate-600">{l.loan_officer_name}</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right text-slate-700">{money(l.budget)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-slate-700">{money(l.funded_to_date)}</div>
                    <div className="mt-1 h-1.5 w-24 ml-auto rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, l.pct_drawn)}%`, background: LENDER_BLUE }} />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{l.pct_drawn}%</div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(l.remaining)}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  </td>
                </tr>
              );
            })}
            {loans.length === 0 && (
              <tr><td colSpan={showOfficer ? 7 : 6} className="px-4 py-10 text-center text-slate-400">No loans to show.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </LenderShell>
  );
}
